import { KeyboardArrowLeft, KeyboardArrowRight } from '@mui/icons-material';
import {
	Box,
	Button,
	Grid,
	IconButton,
	Modal,
	Typography
} from '@mui/material';
import _ from 'lodash';
import {
	CarouselProvider, Image, Slide, Slider 
} from 'pure-react-carousel';
import 'pure-react-carousel/dist/react-carousel.es.css';
import React, { useCallback, useEffect, useState } from 'react';
import { ThreeCircles } from 'react-loader-spinner';
import { useDispatch, useSelector } from 'react-redux';
import imageLogo from '../../assets/images/qbraid.png';
import { fetchPricesList } from '../../Redux/Action/billingAction';
import { sendRGAEvent } from '../../utils/googleAnalytics';

function CardCredit({ cardData, index }) {
	return (
		<Slide index={index}>
			<Box
				display="flex"
				flexDirection="column"
				justifyContent="center"
				alignItems="center"
			>
				<Image
					src={imageLogo}
					alt="credits"
					style={{ width: 100, height: 100 }}
				/>
				<Typography
					fontFamily="Inter"
					fontWeight={700}
					fontSize={24}
					lineHeight={2}
					mt={2}
					color="text.secondary"
				>
					{cardData.unit_amount}&nbsp;Credits
				</Typography>
				<Typography
					fontFamily="Inter"
					fontWeight={500}
					fontSize={21}
					lineHeight={2}
					textAlign="left"
					color="text.primary"
				>
					${parseFloat(cardData.unit_amount / 100).toFixed(2)}
				</Typography>
			</Box>
		</Slide>
	);
}

function CreditModal({ openCredit, handleCloseCredit, handleBuyNow }) {
	const [slideIndex, setSlideIndex] = useState(0);
	const { creditsData } = useSelector((state) => state.billingReducer);
	const { userDetails } = useSelector((state) => state.userReducer);
	const dispatch = useDispatch();

	const loadPriceList = useCallback(() => {
		if (_.isEmpty(creditsData)) {
			dispatch(fetchPricesList());
		}
	}, [creditsData]);

	useEffect(() => {
		if (userDetails?.email && _.isEmpty(creditsData)) {
			loadPriceList();
		}
	}, [userDetails, creditsData]);

	const handleSetCurrentIndex = (nav) => {
		if (nav === 'prev') {
			let index = slideIndex - 1;
			if (+slideIndex === 0) {
				index = +creditsData?.length - 1;
			}
			setSlideIndex(index);
		} else if (nav === 'next') {
			let index = slideIndex + 1;
			if (+slideIndex === +creditsData?.length - 1) {
				index = 0;
			}
			setSlideIndex(index);
		}
	};

	return (
		<Modal
			open={openCredit}
			onClose={handleCloseCredit}
			sx={{
				display: 'flex',
				justifyContent: 'center',
				alignItems: 'center',
				backgroundColor: 'action.disabled'
			}}
		>
			{creditsData?.length === 0 ? (
				<Box
					minHeight={424}
					p={2}
					sx={{
						width: 300,
						backgroundColor: 'background.default',
						borderRadius: 2,
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						justifyContent: 'space-between',
						gap: 2,
						boxShadow: 24
					}}
				>
					<Typography
						fontFamily="Inter"
						fontWeight={700}
						fontSize={24}
						lineHeight={3}
						mt={2}
						color="text.primary"
					>
						qBraid Credits
					</Typography>
					<Box
						style={{
							display: 'flex',
							justifyContent: 'center',
							alignItems: 'center'
						}}
					>
						<ThreeCircles
							height="60"
							width="60"
							color="var(--brand-color-1)"
							ariaLabel="grid-loading"
							radius="12.5"
							wrapperStyle={{}}
							wrapperClass=""
							visible
						/>
					</Box>
					<Typography
						fontFamily="Inter"
						fontWeight={700}
						fontSize={24}
						lineHeight={3}
						mt={2}
						color="text.secondary"
					>
						Loading....
					</Typography>
				</Box>
			) : (
				<Box
					minHeight={424}
					maxWidth={385}
					p={2}
					sx={{
						backgroundColor: 'background.default',
						borderRadius: 2,
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						justifyContent: 'space-between',
						gap: 2,
						boxShadow: 24
					}}
				>
					<Typography
						fontFamily="Inter"
						fontWeight={700}
						fontSize={24}
						lineHeight={3}
						mt={1}
						color="text.secondary"
						cy-data="credit-modal-title"
					>
						Buy Credits
					</Typography>
					<Box
						width="100%"
						height="100%"
						position="relative"
						sx={{ width: 300 }}
					>
						<CarouselProvider
							infinite
							totalSlides={creditsData?.length}
							visibleSlides={1}
							step={1}
							naturalSlideWidth={350}
							naturalSlideHeight={250}
							cy-data="credit-modal-slider-container"
							currentSlide={slideIndex}
						>
							<Slider>
								{creditsData?.map((item, index) => (
									<CardCredit cardData={item} index={index} key={item.id} />
								))}
							</Slider>
							<IconButton
								onClick={() => handleSetCurrentIndex('prev')}
								title="navigate to previous credit option"
								sx={{
									position: 'absolute',
									left: 0,
									top: '33%'
								}}
							>
								<KeyboardArrowLeft />
							</IconButton>
							<IconButton
								title="navigate to next credit option"
								onClick={() => handleSetCurrentIndex('next')}
								sx={{ position: 'absolute', right: 0, top: '33%' }}
							>
								<KeyboardArrowRight />
							</IconButton>
						</CarouselProvider>
					</Box>
					<Grid
						container
						item
						sm={12}
						sx={{
							gap: 1
						}}
					>
						<Grid item xs={12} sm={12} md={4.5} lg={4.5} xl={4.5}>
							<Button
								variant="outlined"
								fullWidth
								size="large"
								shape="rounded"
								sx={{
									textTransform: 'none'
								}}
								onClick={handleCloseCredit}
								cy-data="credit-modal-cancel-btn"
							>
								Cancel
							</Button>
						</Grid>
						<Grid item xs={12} sm={12} md={7} lg={7} xl={7}>
							<Button
								variant="contained"
								shape="rounded"
								fullWidth
								size="large"
								sx={{
									textTransform: 'none'
								}}
								onClick={() => {
									sendRGAEvent('Manage Subscription & Credits', 'Button Click');
									handleBuyNow(creditsData[slideIndex]?.id, 'payment');
								}}
								cy-data="credit-modal-continue-btn"
							>
								Continue
							</Button>
						</Grid>
					</Grid>
				</Box>
			)}
		</Modal>
	);
}

export default CreditModal;
